import { useState, useRef, useEffect } from 'react';
import { bylineName, bylineLink } from '@/lib/byline';
import { BylineWrap } from './BylineWrap';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowUp, MessageSquare, Bookmark, Warehouse, ExternalLink, Play, FileText, MessageCircle, Users, Eye, ShoppingCart, Shield, Flame, BadgeCheck, Sparkles, ThumbsDown, Bell, TrendingUp, CalendarClock, CheckCircle2, Rocket, Link2, Check, AlertTriangle, Plus, Clock, X, Heart, ShieldCheck, ShieldAlert, Star } from 'lucide-react';
import { api, type Post } from '@/lib/api';
import { formatNumber, slugify } from '@/lib/utils';
import { resolvePostHero } from '@/lib/post-hero';
import { getCategoryIcon } from '@/lib/category-icon';
import { RelativeTime } from '@/components/RelativeTime';
import { resolveAffiliateBrand, buildAffiliateUrl, INSURANCE_URLS } from '@/lib/affiliates';
import { ogShareUrl } from '@/components/post/ShareBar';
import { trackAffiliateClick, trackAffiliateFunnelCTA, trackArticleAgeCtr, trackBookmarkAction, trackBookmarkInteraction, trackFeedInteraction, trackGaragePostShare, trackSaveToGarageCta, trackGarageSaveClick, trackShare, trackShareButtonClick, trackOutboundLinkClick, trackLaunchingSoonClick, trackLaunchingThisWeekClick, trackJustLaunchedClick, trackLaunchingTodayClick, trackPriceIntent, trackGarageBadgeImpression, trackGarageBadgeClick, trackTopicFollow, trackFollowConversion, trackFollowCountMilestone, trackSocialProofImpression, trackSocialProofClick, trackReactionBadgeImpression, trackSaveBadgeImpression, trackTrendingArticleClick, trackVideoCardImpression, trackVideoCardClick, trackVideoPlayStarted, trackSponsoredCardImpression, trackSponsoredCardClick, trackSponsoredCardSkip, trackArticleSaved, trackArticleUnsaved, trackSavesBadgeViewed, trackTagClicked, trackAISummaryExpanded, trackSourceQualityBadgeImpression, trackTLDRCardImpression } from '@/lib/analytics';
import { FollowConfirmationToast } from '../FollowConfirmationToast';
import { useAuthStore } from '@/stores/auth-store';
import { GarageShareModal } from './GarageShareModal';
import { ReactionBar } from './ReactionBar';
import { FeedArticleReactions } from './FeedArticleReactions';
import { HotTakePoll } from '../HotTakePoll';
import { PriceDropAlert } from '../PriceDropAlert';
import { usePushOptIn } from '@/hooks/usePushOptIn';
import { outboundUrl } from '@/lib/post-origin';
import { useFeedSourceDiversity } from '@/hooks/useFeedSourceDiversity';
import { WhyThisCard } from './WhyThisCard';

// SEO-380: detect 'upcoming' vehicle articles by title/tag signals so we can
// surface a 🔔 launch-alert bell on the feed card. Cheap heuristic — no
// schema change needed; Strategist + Content-Creator both tag launch posts
// with these terms ('launch', 'upcoming', 'reveal', 'unveil', 'spied',
// 'expected', 'india launch', 'set to launch').
const UPCOMING_TITLE_RE = /\b(upcoming|launch|launched|launching|unveil(?:ed)?|reveal(?:ed)?|spied|expected|set to|coming soon|debut|teased)\b/i;
const UPCOMING_TAGS = new Set(['upcoming', 'launch', 'pre-launch', 'reveal', 'spy-shots']);

// UI-015: Hardcoded launch calendar for confirmed vehicle launches. Posts whose
// brands/title match a vehicle launching within 14 days get an orange "Launching
// {date}" chip. Update this object as new launches are confirmed/complete.
const LAUNCH_CALENDAR: Array<{ keywords: RegExp; brand: string; date: string; label: string }> = [
  // May 2026 (still upcoming)
  { keywords: /honda\s*city/i, brand: 'Honda', date: '2026-05-22', label: 'Honda City Facelift' },
  { keywords: /honda\s*zr[\s-]*v/i, brand: 'Honda', date: '2026-05-22', label: 'Honda ZR-V' },
  { keywords: /ebella|urban\s*cruiser\s*ev/i, brand: 'Toyota', date: '2026-05-28', label: 'Toyota Ebella' },
  // June 2026
  { keywords: /sierra\s*ev/i, brand: 'Tata', date: '2026-06-30', label: 'Tata Sierra EV' },
  { keywords: /bullet\s*650/i, brand: 'Royal Enfield', date: '2026-06-15', label: 'Royal Enfield Bullet 650' },
];

// UI-086: BNCAP (Bharat NCAP) safety ratings — client-side lookup table.
// Matches by vehicle keywords in post title. Data from BNCAP April 2026 results
// + prior rated vehicles. Update as new ratings are published.
const BNCAP_RATINGS: Array<{ keywords: RegExp; stars: number }> = [
  // 5-star
  { keywords: /\bmahindra\s*(xuv\s*700|xuv700)\b/i, stars: 5 },
  { keywords: /\btata\s*(punch|nexon|harrier|safari|curvv)\b/i, stars: 5 },
  { keywords: /\btoyota\s*(innova\s*(hycross|crysta)|urban\s*cruiser\s*hyryder)\b/i, stars: 5 },
  { keywords: /\bhyundai\s*(creta|tucson|verna)\b/i, stars: 5 },
  { keywords: /\bkia\s*(seltos|carens|sonet)\b/i, stars: 5 },
  { keywords: /\bmaruti\s*(brezza|grand\s*vitara|fronx|invicto)\b/i, stars: 5 },
  { keywords: /\bskoda\s*(kushaq|slavia)\b/i, stars: 5 },
  { keywords: /\bvolkswagen\s*(taigun|virtus)\b/i, stars: 5 },
  { keywords: /\bmahindra\s*(xuv\s*3xo|xuv3xo|scorpio[\s-]*n|thar\s*roxx)\b/i, stars: 5 },
  // 4-star
  { keywords: /\btata\s*(tiago|tigor|altroz)\b/i, stars: 4 },
  { keywords: /\bhyundai\s*(i20|exter)\b/i, stars: 4 },
  { keywords: /\bmaruti\s*(swift|dzire|baleno)\b/i, stars: 4 },
  { keywords: /\bmahindra\s*(xuv\s*400|xuv400)\b/i, stars: 4 },
  // 3-star
  { keywords: /\bcitroen\s*e?c3\b/i, stars: 3 },
  { keywords: /\brenault\s*kwid\b/i, stars: 3 },
];

function getBncapStars(post: Post): number | null {
  for (const entry of BNCAP_RATINGS) {
    if (entry.keywords.test(post.title)) return entry.stars;
  }
  return null;
}

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
const FOURTEEN_DAYS_MS = 14 * 24 * 60 * 60 * 1000;
const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

// UI-059: Emoji map for reaction count badge on feed card thumbnail
const REACTION_EMOJI_MAP: Record<string, string> = {
  fire: '\uD83D\uDD25',
  like: '\uD83D\uDC4D',
  interesting: '\uD83E\uDD14',
};

// UI-038: Same-day launch detector — returns info ONLY when the matched
// vehicle launches today (calendar date in IST). Used for the red pulsing
// "LAUNCHING TODAY" hero badge that preempts the orange "Launching {date}"
// chip on launch day. Mutually exclusive with both getLaunchDate (future) and
// getJustLaunchedInfo (past) — a same-day launch is its own category.
function isLaunchingToday(entryDate: string): boolean {
  const launch = new Date(entryDate);
  const now = new Date();
  return (
    launch.getUTCFullYear() === now.getUTCFullYear() &&
    launch.getUTCMonth() === now.getUTCMonth() &&
    launch.getUTCDate() === now.getUTCDate()
  );
}

function getLaunchingTodayInfo(post: Post): { date: string; label: string } | null {
  for (const entry of LAUNCH_CALENDAR) {
    if (!isLaunchingToday(entry.date)) continue;
    const brandMatch = post.brands?.some((b) => b.toLowerCase().includes(entry.brand.toLowerCase()));
    const titleMatch = entry.keywords.test(post.title);
    if (brandMatch || titleMatch) {
      return { date: entry.date, label: entry.label };
    }
  }
  return null;
}

// UI-042: 'Launching This Week' detector — returns info when matched vehicle
// launches within 7 days (but NOT today — that's handled by getLaunchingTodayInfo).
// Shows "Launching Thu" (day-of-week) for more urgency than the 8-14 day range.
function getThisWeekLaunchInfo(post: Post): { date: string; label: string } | null {
  const now = Date.now();
  for (const entry of LAUNCH_CALENDAR) {
    if (isLaunchingToday(entry.date)) continue;
    const launchMs = new Date(entry.date).getTime();
    const daysUntil = launchMs - now;
    if (daysUntil <= 0 || daysUntil > SEVEN_DAYS_MS) continue;
    const brandMatch = post.brands?.some((b) => b.toLowerCase().includes(entry.brand.toLowerCase()));
    const titleMatch = entry.keywords.test(post.title);
    if (brandMatch || titleMatch) {
      return { date: entry.date, label: entry.label };
    }
  }
  return null;
}

function getLaunchDate(post: Post): { date: string; label: string } | null {
  const now = Date.now();
  for (const entry of LAUNCH_CALENDAR) {
    if (isLaunchingToday(entry.date)) continue;
    const launchMs = new Date(entry.date).getTime();
    const daysUntil = launchMs - now;
    // Skip launches within 7 days (handled by getThisWeekLaunchInfo) and beyond 14 days
    if (daysUntil <= SEVEN_DAYS_MS || daysUntil > FOURTEEN_DAYS_MS) continue;
    const brandMatch = post.brands?.some((b) => b.toLowerCase().includes(entry.brand.toLowerCase()));
    const titleMatch = entry.keywords.test(post.title);
    if (brandMatch || titleMatch) {
      return { date: entry.date, label: entry.label };
    }
  }
  return null;
}

// UI-017: Posts matching a vehicle that launched within the past 30 days get a green
// "Just Launched" badge. Mutually exclusive with the "Launching Soon" chip.
function getJustLaunchedInfo(post: Post): { date: string; label: string } | null {
  const now = Date.now();
  for (const entry of LAUNCH_CALENDAR) {
    const launchMs = new Date(entry.date).getTime();
    const sinceLaunch = now - launchMs;
    if (sinceLaunch < 0 || sinceLaunch > THIRTY_DAYS_MS) continue;
    const brandMatch = post.brands?.some((b) => b.toLowerCase().includes(entry.brand.toLowerCase()));
    const titleMatch = entry.keywords.test(post.title);
    if (brandMatch || titleMatch) {
      return { date: entry.date, label: entry.label };
    }
  }
  return null;
}

function formatLaunchDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' });
}

// UI-042: Short day-of-week format for this-week launches — "Thu", "Fri", etc.
function formatLaunchDay(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', { weekday: 'short' });
}

function isUpcomingVehicle(post: Post): boolean {
  if (!post.brands || post.brands.length === 0) return false;
  if (post.tags?.some((t) => UPCOMING_TAGS.has(t.toLowerCase()))) return true;
  return UPCOMING_TITLE_RE.test(post.title);
}

// UI-063: Confirmation toast after saving an article to a garage
function GarageSaveToast({ garageName, onDismiss }: { garageName: string; onDismiss: () => void }) {
  useEffect(() => {
    const timer = setTimeout(onDismiss, 4000);
    return () => clearTimeout(timer);
  }, [onDismiss]);

  return (
    <div className="fixed bottom-6 left-1/2 z-[100] -translate-x-1/2 animate-in fade-in slide-in-from-bottom-4 duration-300">
      <div className="flex items-center gap-3 rounded-2xl border border-primary-500/30 bg-gradient-to-r from-primary-500/90 to-primary-600/90 px-5 py-3 shadow-2xl backdrop-blur">
        <Warehouse className="h-5 w-5 text-white" />
        <span className="text-sm font-bold text-white">Saved to {garageName}!</span>
        <button onClick={onDismiss} aria-label="Dismiss" className="ml-1 rounded-full p-1 text-white/70 hover:text-white hover:bg-white/10">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

// AI-011: TL;DR Card — visually distinct AI summary card with entity chips.
// Shows a compact TL;DR badge, key vehicle/price specs as pills, and an
// expandable summary. Replaces the plain-text AISummaryBlock for posts that
// have AI summaries; falls back to plain excerpt for posts without.
function TLDRCard({ post, index }: { post: Post; index: number }) {
  const [expanded, setExpanded] = useState(false);
  const hasAI = !!post.aiSummaryShort || !!post.aiSummary;
  const canExpand = !!post.aiSummary && post.aiSummary !== post.aiSummaryShort;
  const entities = post.aiEntities;
  const hasEntities = !!(entities?.vehicles?.length || entities?.price_range);
  const impressionRef = useRef(false);

  useEffect(() => {
    if (hasAI && !impressionRef.current) {
      impressionRef.current = true;
      trackTLDRCardImpression(post.id, post.category, hasEntities, index);
    }
  }, [hasAI, hasEntities, post.id, post.category, index]);

  // No AI content — show plain excerpt
  if (!hasAI) {
    return post.excerpt ? (
      <div className="mt-1.5">
        <p className="text-[13px] leading-relaxed text-text-secondary line-clamp-2">{post.excerpt}</p>
      </div>
    ) : null;
  }

  return (
    <div className="mt-2 rounded-lg border border-primary-500/15 bg-primary-500/[0.04] px-3 py-2">
      {/* TL;DR header */}
      <div className="flex items-center gap-1.5 mb-1">
        <Sparkles className="h-3 w-3 text-primary-400" />
        <span className="text-[10px] font-bold uppercase tracking-wider text-primary-400">TL;DR</span>
      </div>

      {/* Summary text */}
      <p className={`text-[13px] leading-relaxed text-text-secondary ${expanded ? '' : 'line-clamp-2'}`}>
        {expanded ? post.aiSummary : (post.aiSummaryShort || post.excerpt)}
      </p>

      {canExpand && !expanded && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setExpanded(true);
            trackAISummaryExpanded(post.id, post.category, 'feed', index);
          }}
          className="mt-0.5 inline-flex items-center gap-1 text-[11px] font-medium text-primary-400 hover:text-primary-300 transition-colors"
        >
          Read more
        </button>
      )}

      {/* Entity chips — vehicles + price range */}
      {hasEntities && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {entities?.vehicles?.slice(0, 3).map((v) => (
            <span
              key={v}
              className="inline-flex items-center rounded-full bg-surface-secondary/60 px-2 py-0.5 text-[10px] font-medium text-text-tertiary"
            >
              {v}
            </span>
          ))}
          {entities?.price_range && (
            <span className="inline-flex items-center rounded-full bg-green-500/10 px-2 py-0.5 text-[10px] font-medium text-green-400">
              {entities.price_range}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

interface PostCardProps {
  post: Post;
  // SEO-379: When a garage's brand/topic matches this post, surface a
  // 'Discuss in [Garage] →' inline CTA. Resolved upstream in FeedList from
  // the cached garage list — keeps the feed → community funnel client-side.
  matchedGarage?: {
    slug: string;
    name: string;
    emoji: string | null;
    memberCount?: number;
  };
  // ANALYTICS-091: Feed position index for sponsored card attribution
  index?: number;
}

const contentTypeConfig = {
  article: { icon: FileText, label: 'Article', color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
  video: { icon: Play, label: 'Video', color: 'bg-gradient-to-r from-red-500 to-red-600' },
  discussion: { icon: MessageCircle, label: 'Discussion', color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
};


const VERIFIED_SOURCES = new Set([
  'Autocar India', 'Team-BHP', 'CarWale', 'BikeWale', 'ZigWheels', 'CarDekho',
  'OverDrive', 'RushLane', 'MotorBeam', 'TopGear India', 'Autocar Professional',
  'PowerDrift', 'Indian Autos Blog', 'GaadiWaadi', 'Vandi Guru',
]);

// UI-047: Headline quality heuristic — classifies headlines as 'verified' (clean,
// trustworthy) or 'sensational' (clickbait patterns). Client-side to avoid DB
// schema changes; deterministic from title text + source.
const CLICKBAIT_RE = /\b(shocking|you won'?t believe|jaw[- ]?dropping|insane|mind[- ]?blow|unbelievable|secret|exposed|truth about|what they don'?t|nobody knows|goes wrong|clickbait)\b/i;
const EXCESSIVE_CAPS_THRESHOLD = 0.5; // >50% uppercase words = sensational
const EXCESSIVE_PUNCTUATION_RE = /[!?]{3,}/;

function getHeadlineQuality(title: string, sourceName?: string): 'verified' | 'sensational' | null {
  // Check for sensational patterns first
  if (CLICKBAIT_RE.test(title)) return 'sensational';
  if (EXCESSIVE_PUNCTUATION_RE.test(title)) return 'sensational';

  const words = title.split(/\s+/).filter((w) => w.length > 1);
  if (words.length > 0) {
    const capsWords = words.filter((w) => w === w.toUpperCase() && /[A-Z]/.test(w));
    // Exclude common abbreviations (EV, SUV, BS7, etc.) — only flag if majority is caps
    if (capsWords.length / words.length > EXCESSIVE_CAPS_THRESHOLD && words.length > 3) {
      return 'sensational';
    }
  }

  // Verified: clean headline from a known editorial source
  if (sourceName && VERIFIED_SOURCES.has(sourceName)) return 'verified';

  return null;
}

// UI-040: Three-tier freshness badge — <2h "Just In" (red pulse), 2-6h
// "Breaking" (amber pulse), 6-24h "Today" (amber, no pulse). The middle tier
// gives time-sensitive content (race reports, launch recaps) visual urgency
// during the peak search-volume window.
function FreshnessBadge({ publishedAt }: { publishedAt: string }) {
  const hoursAgo = (Date.now() - new Date(publishedAt).getTime()) / (1000 * 60 * 60);
  if (hoursAgo < 2) {
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-red-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-red-400 ring-1 ring-red-500/25">
        <span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" />
        Just In
      </span>
    );
  }
  if (hoursAgo < 6) {
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-amber-500/20 px-1.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-amber-300 ring-1 ring-amber-500/40 shadow-sm shadow-amber-500/20 animate-pulse">
        <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
        Breaking
      </span>
    );
  }
  if (hoursAgo < 24) {
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-400 ring-1 ring-amber-500/25">
        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
        Today
      </span>
    );
  }
  return null;
}

// UI-089: Direct WhatsApp share button + Copy Link on feed cards (promoted from dropdown)
function WhatsAppShareButton({ postId, postTitle, category, sourceId }: { postId: string; postTitle: string; category?: string; sourceId?: string }) {
  const waUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(`Check out this on MotoWalla: ${postTitle} ${ogShareUrl(postId, 'whatsapp')}`)}`;

  return (
    <a
      href={waUrl}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => {
        e.stopPropagation();
        trackShare('whatsapp', postId, postTitle, category, sourceId);
        trackShareButtonClick(postId, 'whatsapp', 'feed_card');
      }}
      title="Share on WhatsApp"
      aria-label="Share on WhatsApp"
      className="relative z-10 flex items-center gap-1 rounded-lg px-2 py-1.5 text-[#25D366] transition-all duration-200 hover:bg-[#25D366]/10 hover:scale-110 active:scale-95"
    >
      <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
      </svg>
      <span className="hidden sm:inline text-xs font-medium">WhatsApp</span>
    </a>
  );
}

// UI-093: Twitter/X share button on feed cards
function TwitterShareButton({ postId, postTitle, category, sourceId }: { postId: string; postTitle: string; category?: string; sourceId?: string }) {
  const shareUrl = ogShareUrl(postId, 'twitter');
  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(postTitle)}&url=${encodeURIComponent(shareUrl)}`;

  return (
    <a
      href={twitterUrl}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => {
        e.stopPropagation();
        trackShare('twitter', postId, postTitle, category, sourceId);
        trackShareButtonClick(postId, 'twitter', 'feed_card');
      }}
      title="Share on X (Twitter)"
      aria-label="Share on X"
      className="relative z-10 flex items-center gap-1 rounded-lg px-2 py-1.5 text-text-tertiary transition-all duration-200 hover:bg-[#1DA1F2]/10 hover:text-[#1DA1F2] hover:scale-110 active:scale-95"
    >
      <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
      <span className="hidden sm:inline text-xs font-medium">X</span>
    </a>
  );
}

function CopyLinkButton({ postId, postTitle, category, sourceId }: { postId: string; postTitle: string; category?: string; sourceId?: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(ogShareUrl(postId, 'copy_link'));
      setCopied(true);
      trackShare('copy', postId, postTitle, category, sourceId);
      trackShareButtonClick(postId, 'copy', 'feed_card');
      setTimeout(() => setCopied(false), 1200);
    } catch {
      // silently fail
    }
  };

  return (
    <button
      onClick={handleCopy}
      title={copied ? 'Copied!' : 'Copy link'}
      aria-label={copied ? 'Link copied' : 'Copy link'}
      className="relative z-10 flex items-center gap-1 rounded-lg px-2 py-1.5 text-text-tertiary transition-all duration-200 hover:bg-primary-500/10 hover:text-primary-400 hover:scale-110 active:scale-95"
    >
      {copied ? <Check className="h-4 w-4 text-green-400" /> : <Link2 className="h-4 w-4" />}
      <span className="hidden sm:inline text-xs font-medium">{copied ? 'Copied!' : 'Copy'}</span>
    </button>
  );
}

export function PostCard({ post, matchedGarage, index = 0 }: PostCardProps) {
  const queryClient = useQueryClient();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [showGarageShare, setShowGarageShare] = useState(false);
  const [garageSaveToast, setGarageSaveToast] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [showLaunchAlert, setShowLaunchAlert] = useState(false);
  // AI-002: Toggle between AI-rewritten and original clickbait title
  const [showOriginalTitle, setShowOriginalTitle] = useState(false);
  // UI-051: Toast label shown after follow — null = hidden
  const [followToastLabel, setFollowToastLabel] = useState<string | null>(null);
  const garageBadgeRef = useRef<HTMLAnchorElement>(null);
  // GROWTH-003: Push opt-in on first save/follow action
  const { maybePromptPush } = usePushOptIn();
  // ANALYTICS-043: Feed source diversity tracking
  const { recordSource } = useFeedSourceDiversity();
  // UI-056: Social proof pill ref for impression tracking
  const socialProofRef = useRef<HTMLAnchorElement>(null);
  // UI-059: Reaction count badge ref for impression tracking
  const reactionBadgeRef = useRef<HTMLDivElement>(null);
  // UI-064: Save count badge ref for impression tracking
  const saveBadgeRef = useRef<HTMLSpanElement>(null);
  const videoCardRef = useRef<HTMLDivElement>(null);
  // ANALYTICS-105: Source quality score badge ref
  const sourceQualityRef = useRef<HTMLSpanElement>(null);

  const upvoteMutation = useMutation({
    mutationFn: () => api.posts.upvote(post.id),
    onSuccess: () => {
      trackFeedInteraction('upvote', post.id, post.category, post.source?.url || 'unknown', post.source?.name || 'unknown');
      queryClient.invalidateQueries({ queryKey: ['feed'] });
    },
  });

  const bookmarkMutation = useMutation({
    mutationFn: () => api.posts.bookmark(post.id),
    onSuccess: () => {
      const wasBookmarked = post.userInteraction?.bookmarked;
      trackBookmarkAction(
        wasBookmarked ? 'bookmark_removed' : 'bookmark_added',
        post.id,
        post.category,
        'feed',
      );
      // ANALYTICS-027: Bookmark interaction with vehicle metadata
      trackBookmarkInteraction(
        wasBookmarked ? 'bookmark_removed' : 'bookmark_added',
        post.id,
        post.tags,
        post.category,
        post.vehicleTypes,
        post.aiEntities?.price_range,
        'feed',
      );
      // ANALYTICS-099: Richer save/unsave events with source_domain + position
      const sourceDomain = post.source?.url || 'unknown';
      if (wasBookmarked) {
        trackArticleUnsaved(post.id, sourceDomain);
      } else {
        trackArticleSaved(post.id, sourceDomain, post.category, index);
      }
      queryClient.invalidateQueries({ queryKey: ['feed'] });
      queryClient.invalidateQueries({ queryKey: ['bookmarks'] });
      // GROWTH-003: Prompt push opt-in on first bookmark
      if (!wasBookmarked) maybePromptPush();
    },
  });

  const notRelevantMutation = useMutation({
    mutationFn: () => api.posts.notRelevant(post.id),
    onMutate: () => setDismissed(true),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feed'] });
    },
    onError: () => setDismissed(false),
  });

  // UI-049: Topic/Brand follow from feed card tags
  const { data: userPrefs } = useQuery({
    queryKey: ['user-preferences'],
    queryFn: () => api.users.getPreferences(),
    enabled: isAuthenticated,
    staleTime: 60_000,
  });

  const followTopicMutation = useMutation({
    mutationFn: ({ type, value }: { type: 'brand' | 'topic'; value: string }) =>
      api.users.followTopic(type, value),
    onSuccess: (data, { type, value }) => {
      const action = data.following ? 'follow' : 'unfollow';
      trackTopicFollow(type, value, action, 'feed_card');
      // ANALYTICS-067: conversion funnel events
      trackFollowConversion(type, value, action, 'feed_card');
      queryClient.invalidateQueries({ queryKey: ['user-preferences'] });
      queryClient.invalidateQueries({ queryKey: ['feed'] });
      // UI-051: show confirmation toast on follow (not unfollow)
      if (data.following) {
        setFollowToastLabel(value);
        // ANALYTICS-067: milestone tracking — count followed items
        const currentBrands = userPrefs?.followedBrands?.length ?? 0;
        const currentTopics = userPrefs?.followedTopics?.length ?? 0;
        const totalFollows = currentBrands + currentTopics + 1; // +1 for the one just followed
        trackFollowCountMilestone(totalFollows);
      }
    },
  });

  // UI-045 / ANALYTICS-062: Fire garage_badge_impression when the badge enters
  // the viewport. One-shot — disconnects after first intersection.
  useEffect(() => {
    const el = garageBadgeRef.current;
    if (!el || !post.garageCount) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          trackGarageBadgeImpression(post.id, post.garageCount!);
          observer.disconnect();
        }
      },
      { threshold: 0.5 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [post.id, post.garageCount]);

  if (dismissed) return null;

  // T-128: null when this byline has no real destination (see lib/byline).
  const bylineHref = bylineLink(post);
  const isUpvoted = post.userInteraction?.upvoted;
  const isBookmarked = post.userInteraction?.bookmarked;
  const firstBrand = post.brands?.[0];
  const showSocialProof = !!firstBrand && (post.brandFollowerCount ?? 0) >= 5;

  // ANALYTICS-071: Social proof pill impression — one-shot on viewport entry
  useEffect(() => {
    const el = socialProofRef.current;
    if (!el || !showSocialProof) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          trackSocialProofImpression(post.id, firstBrand!, post.brandFollowerCount!);
          observer.disconnect();
        }
      },
      { threshold: 0.5 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [post.id, firstBrand, post.brandFollowerCount, showSocialProof]);

  // UI-064 + UI-078: Save count badge — social proof when bookmarkCount >= 3
  const showSaveBadge = (post.bookmarkCount ?? 0) >= 3;
  useEffect(() => {
    const el = saveBadgeRef.current;
    if (!el || !showSaveBadge) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          trackSaveBadgeImpression(post.id, post.bookmarkCount);
          // ANALYTICS-099: saves_badge_viewed with spec-matching payload
          trackSavesBadgeViewed(post.id, post.bookmarkCount ?? 0);
          observer.disconnect();
        }
      },
      { threshold: 0.5 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [post.id, post.bookmarkCount, showSaveBadge]);

  // ANALYTICS-074: Reaction badge impression — one-shot on viewport entry
  const showReactionBadge = (post.reactionCount ?? 0) > 0;
  useEffect(() => {
    const el = reactionBadgeRef.current;
    if (!el || !showReactionBadge) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          trackReactionBadgeImpression(post.id, post.topReaction!, post.reactionCount!);
          observer.disconnect();
        }
      },
      { threshold: 0.5 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [post.id, post.topReaction, post.reactionCount, showReactionBadge]);

  // ANALYTICS-090: Video card impression — one-shot on viewport entry
  const isVideo = post.contentType === 'video';
  useEffect(() => {
    const el = videoCardRef.current;
    if (!el || !isVideo) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          trackVideoCardImpression(post.id, post.category, post.duration ?? null, 'feed_list');
          observer.disconnect();
        }
      },
      { threshold: 0.5 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [post.id, post.category, post.duration, isVideo]);

  // ANALYTICS-091: Sponsored card impression — one-shot on viewport entry.
  // Pre-wired for MON-001; fires only when post.isSponsored is true.
  const isSponsored = !!post.isSponsored;
  const sponsoredRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = sponsoredRef.current ?? videoCardRef.current;
    if (!el || !isSponsored) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          trackSponsoredCardImpression(post.id, index, post.sponsorBrand ?? '', post.category);
          observer.disconnect();
        }
      },
      { threshold: 0.5 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [post.id, post.category, isSponsored, post.sponsorBrand, index]);

  const affiliateBrand = firstBrand ? resolveAffiliateBrand(firstBrand) : null;
  // ANALYTICS-028: vehicleSlug for price-intent heat-mapping. Brand-prefixed slug
  // of the title aggregates the same vehicle across multiple posts. Falls back
  // to the title alone when no brand is tagged.
  const vehicleSlug = slugify(firstBrand ? `${firstBrand} ${post.title}` : post.title);
  const isComparisonPost = /\bvs\b/i.test(post.title);
  const insuranceUrl = INSURANCE_URLS[post.category];
  const isUpcoming = isUpcomingVehicle(post);
  // UI-038 / ANALYTICS-054: same-day launch takes precedence over future "Launching {date}"
  // chip and past "Just Launched" badge. trackLaunchingTodayClick carries
  // is_launch_day=true so day-1 vs evergreen traffic can be segmented.
  // UI-042: thisWeekLaunchInfo (1-7d) sits between today and the 8-14d range.
  const launchingTodayInfo = getLaunchingTodayInfo(post);
  const thisWeekLaunchInfo = !launchingTodayInfo ? getThisWeekLaunchInfo(post) : null;
  const launchInfo = !launchingTodayInfo && !thisWeekLaunchInfo ? getLaunchDate(post) : null;
  const justLaunchedInfo = !launchingTodayInfo && !thisWeekLaunchInfo && !launchInfo ? getJustLaunchedInfo(post) : null;
  const isHotDebate = post.commentCount >= 10;
  const isHot = !isHotDebate && (post.upvoteCount >= 10 || post.viewCount >= 500 || post.commentCount >= 5);
  const isAiPick = (post.aiRelevanceScore ?? 0) >= 0.7;
  // UI-047: Headline quality indicator
  const headlineQuality = getHeadlineQuality(post.title, post.source?.name);
  // UI-086: BNCAP stars — prefer backend value, fall back to client-side lookup
  const bncapStars = post.bncapStars ?? getBncapStars(post);
  // ANALYTICS-105: Source quality tier — trusted (≥0.90), low (<0.70), or null (no badge)
  const sourceReputation = post.source?.reputationScore != null ? Number(post.source.reputationScore) : null;
  const sourceQualityTier = sourceReputation !== null
    ? sourceReputation >= 0.90 ? 'trusted' as const
    : sourceReputation < 0.70 ? 'low' as const
    : null
    : null;
  // ANALYTICS-105: Source quality badge impression tracking
  useEffect(() => {
    const el = sourceQualityRef.current;
    if (!el || !sourceQualityTier) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          trackSourceQualityBadgeImpression(
            post.id,
            post.source?.id ?? '',
            post.source?.name ?? '',
            sourceReputation!,
            sourceQualityTier,
            index,
          );
          observer.disconnect();
        }
      },
      { threshold: 0.5 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [post.id, post.source?.id, post.source?.name, sourceReputation, sourceQualityTier, index]);
  const typeConfig = contentTypeConfig[post.contentType] || contentTypeConfig.article;
  const TypeIcon = typeConfig.icon;
  // T-249: no title card on a card — the headline is printed inches away, so a
  // generated title image would show the same sentence twice. Real thumbnail
  // or the icon treatment; the Unsplash pool is deleted.
  const hero = resolvePostHero(post);
  const [heroFailed, setHeroFailed] = useState(false);

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  return (
    <article className="group relative overflow-hidden rounded-xl border border-border-primary bg-gradient-to-br from-bg-secondary to-bg-secondary/95 transition-all duration-300 ease-out hover:border-primary-500/40 hover:shadow-xl hover:shadow-primary-500/10 hover:-translate-y-0.5">
      <div className="flex">
        {/* Thumbnail Section */}
        <div ref={videoCardRef} className="relative w-48 flex-shrink-0">
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent z-10 transition-opacity duration-300 group-hover:from-black/50" />
          {hero && !heroFailed ? (
            <img
              src={hero.src}
              alt={hero.alt}
              className="h-full w-full object-cover transition-all duration-500 ease-out group-hover:scale-110 group-hover:brightness-110"
              onError={() => setHeroFailed(true)}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-bg-tertiary">
              {(() => {
                const CategoryIcon = getCategoryIcon(post.category);
                return <CategoryIcon className="h-8 w-8 text-text-tertiary" />;
              })()}
            </div>
          )}

          {/* Content Type Badge */}
          <div className={`absolute top-2 left-2 z-20 flex items-center gap-1.5 rounded-lg ${typeConfig.color} px-2.5 py-1 shadow-lg backdrop-blur-sm border border-white/20`}>
            <TypeIcon className="h-3.5 w-3.5 text-white drop-shadow-sm" />
            <span className="text-[10px] font-bold uppercase tracking-wide text-white drop-shadow-sm">
              {typeConfig.label}
            </span>
          </div>

          {/* Duration for videos */}
          {post.contentType === 'video' && post.duration && (
            <div className="absolute bottom-2 right-2 z-20 rounded-md bg-black/85 px-2 py-0.5 text-xs font-semibold text-white backdrop-blur-sm shadow-lg tabular-nums">
              {formatDuration(post.duration)}
            </div>
          )}

          {/* Play button overlay for videos */}
          {post.contentType === 'video' && (
            <div
              className="absolute inset-0 z-10 flex items-center justify-center opacity-0 transition-all duration-300 group-hover:opacity-100"
              onClick={() => {
                trackVideoCardClick(post.id, post.category, post.duration ?? null, 'feed_list');
                trackVideoPlayStarted(post.id, post.category, post.duration ?? null, 'feed_list');
              }}
            >
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white/95 shadow-xl shadow-black/30 ring-4 ring-white/30 transition-transform duration-300 group-hover:scale-110 backdrop-blur-sm">
                <Play className="h-6 w-6 text-gray-900 ml-0.5 drop-shadow-sm" fill="currentColor" />
              </div>
            </div>
          )}

          {/* UI-059: Reaction count badge — top emoji + total count */}
          {showReactionBadge && (
            <div
              ref={reactionBadgeRef}
              className={`absolute ${post.contentType === 'video' && post.duration ? 'bottom-2 left-2' : 'bottom-2 right-2'} z-20 flex items-center gap-1 rounded-md bg-black/70 px-1.5 py-0.5 text-xs text-white backdrop-blur-sm shadow-lg`}
            >
              <span>{REACTION_EMOJI_MAP[post.topReaction!]}</span>
              <span className="tabular-nums font-medium">{post.reactionCount}</span>
            </div>
          )}

          {/* UI-063: Save to Garage quick-action — floating icon on hover */}
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              trackGaragePostShare(post.id, '', 'initiated', undefined, post.category);
              trackSaveToGarageCta('click', post.id, isAuthenticated, 'feed');
              trackGarageSaveClick(post.id, 'feed_card', 0, post.source?.url || 'unknown', post.source?.name || 'unknown');
              setShowGarageShare(true);
            }}
            title="Save to Garage"
            aria-label="Save to Garage"
            className="absolute top-2 right-2 z-20 flex h-8 w-8 items-center justify-center rounded-lg bg-black/60 text-white/80 opacity-0 backdrop-blur-sm transition-all duration-200 group-hover:opacity-100 hover:bg-primary-500/80 hover:text-white hover:scale-110 active:scale-95"
          >
            <Warehouse className="h-4 w-4" />
          </button>
        </div>

        {/* Content Section */}
        <div className="flex min-w-0 flex-1 flex-col p-4">
          {/* Source & Time */}
          <div className="flex items-center gap-2 text-xs">
            {/* T-128: a link ONLY when there is somewhere real to go — the
                writer's profile for our originals, the source page for
                aggregated posts. The old `: '#'` fallback rendered a clickable
                affordance that did nothing on all 37 of our originals
                (decision 105). Identical markup either way; it simply is not a
                link when it cannot be one. */}
            <BylineWrap
              href={bylineHref}
              onClick={(e) => e.stopPropagation()}
              className="relative z-10 flex items-center gap-2 rounded hover:bg-bg-tertiary -ml-1 px-1 transition-colors"
            >
              {post.source?.logoUrl ? (
                <img
                  src={post.source.logoUrl}
                  alt=""
                  className="h-4 w-4 rounded-sm"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              ) : (
                <div className="h-4 w-4 rounded-sm bg-gradient-to-br from-primary-500 to-primary-600" />
              )}
              <span className="font-medium text-text-secondary hover:text-primary-400 transition-colors duration-200 hover:underline hover:underline-offset-2">
                {bylineName(post)}
              </span>
              {post.source?.name && VERIFIED_SOURCES.has(post.source.name) && (
                <span title="Verified Source" className="text-emerald-400">
                  <BadgeCheck className="h-3.5 w-3.5" />
                </span>
              )}
              {post.source?.name?.startsWith('r/') && (
                <span
                  title="Community post from Reddit"
                  className="inline-flex items-center rounded-md bg-orange-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-orange-400 ring-1 ring-orange-500/25"
                >
                  Reddit
                </span>
              )}
            </BylineWrap>
            {/* ANALYTICS-105: Source quality score badge */}
            {sourceQualityTier === 'trusted' && (
              <span
                ref={sourceQualityRef}
                title={`Trusted source (${((sourceReputation ?? 0) * 100).toFixed(0)}% quality score)`}
                className="inline-flex items-center gap-0.5 rounded-md bg-emerald-500/10 px-1 py-0.5 text-[10px] font-medium text-emerald-400 ring-1 ring-emerald-500/20"
              >
                <ShieldCheck className="h-3 w-3" />
                Trusted
              </span>
            )}
            {sourceQualityTier === 'low' && (
              <span
                ref={sourceQualityRef}
                title={`Low quality source (${((sourceReputation ?? 0) * 100).toFixed(0)}% quality score)`}
                className="inline-flex items-center gap-0.5 rounded-md bg-amber-500/10 px-1 py-0.5 text-[10px] font-medium text-amber-400 ring-1 ring-amber-500/20"
              >
                <ShieldAlert className="h-3 w-3" />
                Low Quality
              </span>
            )}
            {/* UI-086: BNCAP safety star rating badge */}
            {bncapStars != null && bncapStars > 0 && (
              <span
                title={`BNCAP ${bncapStars}-Star Safety Rating`}
                className="inline-flex items-center gap-0.5 rounded-md bg-orange-500/10 px-1.5 py-0.5 text-[10px] font-bold text-orange-400 ring-1 ring-orange-500/20"
              >
                <Star className="h-3 w-3" fill="currentColor" />
                {bncapStars}-Star
              </span>
            )}
            <span className="text-text-tertiary">•</span>
            <RelativeTime date={post.publishedAt} className="text-text-tertiary" />
            <FreshnessBadge publishedAt={post.publishedAt} />
            {post.isTrending && (
              <span className="inline-flex items-center gap-1 rounded-md bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-400 ring-1 ring-emerald-500/25">
                <TrendingUp className="h-3 w-3" />
                Trending
              </span>
            )}
            {isHotDebate && (
              <span className="inline-flex items-center gap-1 rounded-md bg-rose-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-rose-400 ring-1 ring-rose-500/25">
                <Flame className="h-3 w-3" />
                Hot Debate
              </span>
            )}
            {isHot && (
              <span className="inline-flex items-center gap-1 rounded-md bg-orange-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-orange-400 ring-1 ring-orange-500/25">
                <Flame className="h-3 w-3" />
                Hot
              </span>
            )}
            {isAiPick && (
              <span className="inline-flex items-center gap-1 rounded-md bg-violet-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-violet-400 ring-1 ring-violet-500/25">
                <Sparkles className="h-3 w-3" />
                AI Pick
              </span>
            )}
            {post.headlineRewritten && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (post.originalTitle) setShowOriginalTitle((v) => !v);
                }}
                title={showOriginalTitle ? 'Show AI-improved headline' : (post.originalTitle ? 'Click to see original headline' : 'Headline rewritten for clarity by MotoWalla AI')}
                className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ring-1 transition-colors cursor-pointer ${
                  showOriginalTitle
                    ? 'bg-amber-500/15 text-amber-400 ring-amber-500/25'
                    : 'bg-cyan-500/15 text-cyan-400 ring-cyan-500/25 hover:bg-cyan-500/25'
                }`}
              >
                <Sparkles className="h-3 w-3" />
                {showOriginalTitle ? 'Original' : 'AI-improved'}
              </button>
            )}
            {/* UI-047: Headline quality indicator */}
            {headlineQuality === 'verified' && (
              <span
                title="Verified editorial headline"
                className="inline-flex items-center gap-1 rounded-md bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-400 ring-1 ring-emerald-500/25"
              >
                <BadgeCheck className="h-3 w-3" />
                Verified
              </span>
            )}
            {headlineQuality === 'sensational' && (
              <span
                title="Headline may be sensational — read with caution"
                className="inline-flex items-center gap-1 rounded-md bg-yellow-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-yellow-400 ring-1 ring-yellow-500/25"
              >
                <AlertTriangle className="h-3 w-3" />
                Sensational
              </span>
            )}
            {(post.garageActivityCount ?? 0) > 0 && (
              <span
                title={`${post.garageActivityCount} garage${post.garageActivityCount === 1 ? '' : 's'} discussing this`}
                className="inline-flex items-center gap-1 rounded-md bg-indigo-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-indigo-400 ring-1 ring-indigo-500/25"
              >
                <Users className="h-3 w-3" />
                {post.garageActivityCount} {post.garageActivityCount === 1 ? 'Garage' : 'Garages'}
              </span>
            )}
            {/* UI-064 + UI-078: Save count badge — social proof for popular articles (≥3 saves) */}
            {showSaveBadge && (
              <span
                ref={saveBadgeRef}
                title={`${post.bookmarkCount} users saved this article`}
                className="inline-flex items-center gap-1 rounded-md bg-pink-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-pink-400 ring-1 ring-pink-500/25"
              >
                <Heart className="h-3 w-3" />
                {post.bookmarkCount} Saves
              </span>
            )}
            {/* ANALYTICS-091: Sponsored label badge */}
            {isSponsored && (
              <span
                className="inline-flex items-center gap-1 rounded-md bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-400 ring-1 ring-amber-500/25"
              >
                Sponsored
              </span>
            )}
            {launchingTodayInfo && (
              <span
                title={`${launchingTodayInfo.label} launches today!`}
                onClick={() => trackLaunchingTodayClick(post.id, launchingTodayInfo.label, launchingTodayInfo.date)}
                className="inline-flex items-center gap-1 rounded-md bg-red-500/20 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-red-300 ring-1 ring-red-500/40 shadow-sm shadow-red-500/20 animate-pulse"
              >
                <Rocket className="h-3 w-3" />
                Launching Today
              </span>
            )}
            {thisWeekLaunchInfo && (
              <span
                title={`${thisWeekLaunchInfo.label} launches ${formatLaunchDay(thisWeekLaunchInfo.date)}`}
                onClick={() => trackLaunchingThisWeekClick(post.id, thisWeekLaunchInfo.label, thisWeekLaunchInfo.date)}
                className="inline-flex items-center gap-1 rounded-md bg-amber-500/20 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-amber-300 ring-1 ring-amber-500/40 shadow-sm shadow-amber-500/20 animate-pulse"
              >
                <CalendarClock className="h-3 w-3" />
                Launching {formatLaunchDay(thisWeekLaunchInfo.date)}
              </span>
            )}
            {launchInfo && (
              <span
                title={`${launchInfo.label} launches ${formatLaunchDate(launchInfo.date)}`}
                onClick={() => trackLaunchingSoonClick(post.id, launchInfo.label, launchInfo.date)}
                className="inline-flex items-center gap-1 rounded-md bg-orange-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-orange-400 ring-1 ring-orange-500/25"
              >
                <CalendarClock className="h-3 w-3" />
                Launching {formatLaunchDate(launchInfo.date)}
              </span>
            )}
            {justLaunchedInfo && (
              <span
                title={`${justLaunchedInfo.label} launched ${formatLaunchDate(justLaunchedInfo.date)}`}
                onClick={() => trackJustLaunchedClick(post.id, justLaunchedInfo.label, justLaunchedInfo.date)}
                className="inline-flex items-center gap-1 rounded-md bg-green-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-green-400 ring-1 ring-green-500/25"
              >
                <CheckCircle2 className="h-3 w-3" />
                Just Launched
              </span>
            )}
            {/* UI-050: Reading time estimate pill badge */}
            {(() => {
              const mins = post.readTime ?? (post.excerpt && post.excerpt.split(/\s+/).length > 60
                ? Math.max(1, Math.round(post.excerpt.split(/\s+/).length / 200))
                : null);
              return mins ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-text-tertiary/10 px-1.5 py-0.5 text-[10px] font-medium text-text-tertiary ring-1 ring-text-tertiary/20">
                  <Clock className="h-3 w-3" />
                  ~{mins} min read
                </span>
              ) : null;
            })()}
            {/* UI-052: Freshness badge — 'Just In' (<2h) or 'New' (<24h) */}
            {(() => {
              const ageMs = Date.now() - new Date(post.publishedAt).getTime();
              const TWO_HOURS = 2 * 60 * 60 * 1000;
              const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000;
              if (ageMs < TWO_HOURS) {
                return (
                  <span className="inline-flex items-center gap-1 rounded-full bg-red-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-red-400 ring-1 ring-red-500/25 animate-pulse">
                    <Flame className="h-3 w-3" />
                    Just In
                  </span>
                );
              }
              if (ageMs < TWENTY_FOUR_HOURS) {
                return (
                  <span className="inline-flex items-center gap-1 rounded-full bg-orange-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-orange-400 ring-1 ring-orange-500/25">
                    <Sparkles className="h-3 w-3" />
                    New
                  </span>
                );
              }
              return null;
            })()}
            {post.viewCount > 0 && (
              <>
                <span className="text-text-tertiary">•</span>
                <span className="flex items-center gap-1 text-text-tertiary">
                  <Eye className="h-3 w-3" />
                  {formatNumber(post.viewCount)}
                </span>
              </>
            )}
          </div>

          {/* AI-002: "Why This?" explainability tooltip */}
          <WhyThisCard post={post} />

          {/* AI-001: Garage-matched personalization label */}
          {post.garageMatch && (
            <Link
              to={`/garage/${post.garageMatch.slug}`}
              onClick={(e) => e.stopPropagation()}
              className="mt-1.5 inline-flex w-fit items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-[11px] font-medium text-amber-400 ring-1 ring-amber-500/20 transition-colors hover:bg-amber-500/20"
            >
              <span aria-hidden>{post.garageMatch.emoji || '🏠'}</span>
              Because you're in {post.garageMatch.name}
            </Link>
          )}

          {/* Title — AI-002: show original clickbait title when toggled */}
          <h3 className="mt-2.5 line-clamp-2 text-[16px] font-semibold leading-snug tracking-tight text-text-primary transition-colors duration-200 group-hover:text-primary-400">
            <Link
              to={`/post/${post.id}`}
              onClick={() => {
                trackFeedInteraction('click', post.id, post.category, post.source?.url || 'unknown', post.source?.name || 'unknown');
                recordSource(post.source?.name || 'unknown');
                trackPriceIntent('specs_click', vehicleSlug, post.id, 'feed');
                // ANALYTICS-056: article freshness vs CTR — hours since publish + category + source
                const hoursSincePublish = (Date.now() - new Date(post.publishedAt).getTime()) / (1000 * 60 * 60);
                trackArticleAgeCtr(post.id, hoursSincePublish, post.category, post.source?.url || 'unknown', post.source?.name || 'unknown');
                if (isComparisonPost) {
                  trackPriceIntent('compare_click', vehicleSlug, post.id, 'feed');
                }
                // ANALYTICS-084: Trending article click attribution
                if (post.isTrending) {
                  trackTrendingArticleClick(post.id, post.category, post.tags?.[0]);
                }
                // ANALYTICS-091: Sponsored card click attribution
                if (isSponsored) {
                  trackSponsoredCardClick(post.id, post.sponsorCtaType ?? 'see_price', post.sponsorBrand ?? '', post.category);
                }
              }}
              className="after:absolute after:inset-0"
            >
              {showOriginalTitle && post.originalTitle ? (
                <span className="text-amber-300/90 line-through decoration-amber-500/40">{post.originalTitle}</span>
              ) : (
                post.title
              )}
            </Link>
          </h3>
          {/* AI-002: Show improved title below when viewing original */}
          {showOriginalTitle && post.originalTitle && post.originalTitle !== post.title && (
            <p className="mt-0.5 text-[13px] text-cyan-400/80 font-medium">
              <Sparkles className="inline h-3 w-3 mr-1" />
              {post.title}
            </p>
          )}

          {/* AI-011: TL;DR Card — styled AI summary with entity chips */}
          {(post.aiSummaryShort || post.aiSummary || post.excerpt) && (
            <TLDRCard post={post} index={index} />
          )}

          {/* Tags with follow buttons (UI-049) */}
          <div className="mt-2 flex flex-wrap gap-1.5">
            {post.brands?.slice(0, 2).map((brand) => {
              const isFollowed = userPrefs?.followedBrands?.includes(brand);
              return (
                <span key={brand} className="inline-flex items-center gap-0">
                  <Link
                    to={`/tag/${slugify(brand)}`}
                    onClick={(e) => { e.stopPropagation(); trackTagClicked(brand, post.id, 'article_card'); }}
                    className={`inline-flex items-center rounded-l-md px-2 py-0.5 text-[11px] font-semibold transition-all duration-200 cursor-pointer ${
                      isFollowed
                        ? 'bg-primary-500/25 text-primary-300 ring-1 ring-primary-500/40'
                        : 'bg-gradient-to-r from-primary-500/15 to-primary-500/5 text-primary-400 ring-1 ring-primary-500/20 hover:ring-primary-500/40 hover:bg-primary-500/20'
                    }`}
                  >
                    {brand}
                  </Link>
                  {isAuthenticated && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        followTopicMutation.mutate({ type: 'brand', value: brand });
                      }}
                      className={`relative z-10 inline-flex items-center rounded-r-md px-1 py-0.5 text-[11px] transition-all duration-200 cursor-pointer ${
                        isFollowed
                          ? 'bg-primary-500/25 text-primary-300 ring-1 ring-primary-500/40 hover:bg-red-500/20 hover:text-red-400'
                          : 'bg-primary-500/10 text-primary-500 ring-1 ring-primary-500/20 hover:bg-primary-500/25'
                      }`}
                      title={isFollowed ? `Unfollow ${brand}` : `Follow ${brand}`}
                    >
                      {isFollowed ? <Check className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
                    </button>
                  )}
                </span>
              );
            })}
            {post.tags?.slice(0, 2).map((tag) => {
              const isFollowed = userPrefs?.followedTopics?.includes(tag);
              return (
                <span key={tag} className="inline-flex items-center gap-0">
                  <Link
                    to={`/tag/${slugify(tag)}`}
                    onClick={(e) => { e.stopPropagation(); trackTagClicked(tag, post.id, 'article_card'); }}
                    className={`inline-flex items-center rounded-l-md px-2 py-0.5 text-[11px] font-medium transition-colors duration-200 cursor-pointer ${
                      isFollowed
                        ? 'bg-bg-tertiary text-text-secondary ring-1 ring-primary-500/30'
                        : 'bg-bg-tertiary/80 text-text-tertiary ring-1 ring-border-primary/50 hover:text-text-secondary hover:bg-bg-tertiary'
                    }`}
                  >
                    {tag}
                  </Link>
                  {isAuthenticated && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        followTopicMutation.mutate({ type: 'topic', value: tag });
                      }}
                      className={`relative z-10 inline-flex items-center rounded-r-md px-1 py-0.5 text-[11px] transition-all duration-200 cursor-pointer ${
                        isFollowed
                          ? 'bg-bg-tertiary text-text-secondary ring-1 ring-primary-500/30 hover:bg-red-500/20 hover:text-red-400'
                          : 'bg-bg-tertiary/60 text-text-tertiary ring-1 ring-border-primary/50 hover:bg-bg-tertiary hover:text-text-secondary'
                      }`}
                      title={isFollowed ? `Unfollow ${tag}` : `Follow ${tag}`}
                    >
                      {isFollowed ? <Check className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
                    </button>
                  )}
                </span>
              );
            })}
          </div>

          {/* UI-056: Social proof pill — 'N members follow Brand' */}
          {showSocialProof && (
            <Link
              ref={socialProofRef}
              to={`/tag/${slugify(firstBrand!)}`}
              onClick={(e) => {
                e.stopPropagation();
                trackSocialProofClick(post.id, firstBrand!, post.brandFollowerCount!);
              }}
              className="relative z-10 mt-1.5 inline-flex w-fit items-center gap-1.5 rounded-full bg-text-tertiary/8 px-2.5 py-1 text-[11px] font-medium text-text-tertiary ring-1 ring-text-tertiary/15 transition-colors hover:bg-text-tertiary/15 hover:text-text-secondary"
            >
              <Users className="h-3 w-3" />
              {post.brandFollowerCount} members follow {firstBrand}
            </Link>
          )}

          {/* SEO-379: Matched garage CTA — feed → community funnel */}
          {matchedGarage && (
            <Link
              to={`/garage/${matchedGarage.slug}`}
              onClick={(e) => e.stopPropagation()}
              className="relative z-10 mt-2 inline-flex w-fit items-center gap-1.5 rounded-md bg-primary-500/10 px-2 py-1 text-[11px] font-medium text-primary-400 ring-1 ring-primary-500/20 transition-colors hover:bg-primary-500/20 hover:text-primary-300"
            >
              <span aria-hidden>{matchedGarage.emoji || '🚗'}</span>
              Discuss in {matchedGarage.name}
              {matchedGarage.memberCount != null && matchedGarage.memberCount > 0 && (
                <span className="ml-1 text-[10px] opacity-70">{formatNumber(matchedGarage.memberCount)} members</span>
              )}
              <span aria-hidden>→</span>
            </Link>
          )}

          {/* Actions Bar */}
          <div className="mt-auto flex items-center gap-1 pt-3">
            {/* Upvote */}
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                upvoteMutation.mutate();
              }}
              className={`relative z-10 flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-semibold transition-all duration-200 ${
                isUpvoted
                  ? 'bg-gradient-to-r from-upvote/20 to-upvote/10 text-upvote shadow-sm shadow-upvote/20 ring-1 ring-upvote/30'
                  : 'text-text-tertiary hover:bg-upvote/10 hover:text-upvote active:scale-95'
              }`}
            >
              <ArrowUp className={`h-4 w-4 transition-transform duration-200 ${isUpvoted ? 'fill-current scale-110' : ''}`} />
              <span className="tabular-nums">{post.upvoteCount}</span>
            </button>

            {/* Comments */}
            <Link
              to={`/post/${post.id}#comments`}
              onClick={(e) => e.stopPropagation()}
              className="relative z-10 flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium text-text-tertiary transition-all duration-200 hover:bg-bg-tertiary hover:text-text-primary active:scale-95"
            >
              <MessageSquare className="h-4 w-4" />
              <span className="tabular-nums">{post.commentCount}</span>
            </Link>

            {/* Reactions — COMM-003 + UI-082: public counts */}
            <ReactionBar
              postId={post.id}
              initialReaction={post.userInteraction?.reaction ?? null}
              surface="feed"
              invalidateKeys={[['feed']]}
              reactionCounts={post.reactionCounts}
            />

            {/* COMM-009: Article emoji reactions — visitor-based, lazy-loaded */}
            <FeedArticleReactions postId={post.id} />

            {/* Hot Takes — ENGAGE-017 */}
            <HotTakePoll postId={post.id} />

            {/* Garage Activity Chip */}
            {post.garageActivityCount && post.garageActivityCount >= 2 && (
              <Link
                to={`/post/${post.id}`}
                onClick={(e) => e.stopPropagation()}
                className="relative z-10 flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium text-text-tertiary transition-all duration-200 hover:bg-primary-500/10 hover:text-primary-400 active:scale-95"
              >
                <Users className="h-4 w-4" />
                <span className="tabular-nums">{post.garageActivityCount}</span>
                <span className="hidden sm:inline text-xs">Garages</span>
              </Link>
            )}

            {/* UI-045: Garage member count badge — social proof for community discovery */}
            {(post.garageCount ?? 0) > 0 && (
              <Link
                ref={garageBadgeRef}
                to={matchedGarage ? `/garage/${matchedGarage.slug}` : `/post/${post.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  trackGarageBadgeClick(post.id, post.garageCount!);
                }}
                className="relative z-10 flex items-center gap-1.5 rounded-lg bg-indigo-500/10 px-2.5 py-1.5 text-sm font-medium text-indigo-400 ring-1 ring-indigo-500/20 transition-all duration-200 hover:bg-indigo-500/20 hover:text-indigo-300 active:scale-95"
              >
                <Users className="h-4 w-4" />
                <span className="tabular-nums">{post.garageCount}</span>
                <span className="hidden sm:inline text-xs">in Garage</span>
              </Link>
            )}

            <div className="flex-1" />

            {/* Bookmark */}
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                bookmarkMutation.mutate();
              }}
              className={`relative z-10 flex items-center gap-1 rounded-lg px-2 py-1.5 transition-all duration-200 ${
                isBookmarked
                  ? 'bg-gradient-to-br from-bookmark/25 to-bookmark/10 text-bookmark shadow-sm shadow-bookmark/25 ring-1 ring-bookmark/30'
                  : 'text-text-tertiary hover:bg-bookmark/10 hover:text-bookmark hover:scale-110 active:scale-95'
              }`}
            >
              <Bookmark className={`h-4 w-4 transition-all duration-200 ${isBookmarked ? 'fill-current' : ''}`} />
              {post.bookmarkCount > 0 && (
                <span className="text-xs tabular-nums">{post.bookmarkCount}</span>
              )}
            </button>

            {/* Save to Garage */}
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                trackGaragePostShare(post.id, '', 'initiated', undefined, post.category);
                trackSaveToGarageCta('click', post.id, isAuthenticated, 'feed');
                trackGarageSaveClick(post.id, 'feed_card', 0, post.source?.url || 'unknown', post.source?.name || 'unknown');
                setShowGarageShare(true);
              }}
              className="relative z-10 flex items-center gap-1 rounded-lg px-2 py-1.5 text-text-tertiary transition-all duration-200 hover:bg-primary-500/10 hover:text-primary-400 hover:scale-110 active:scale-95"
            >
              <Warehouse className="h-4 w-4" />
              <span className="hidden sm:inline text-xs font-medium">Garage</span>
            </button>

            {/* SEO-380: Launch alert bell — only on upcoming/launch posts.
                Tap toggles inline PriceDropAlert below; reuses subscribe API. */}
            {isUpcoming && firstBrand && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowLaunchAlert((v) => !v);
                }}
                title="Set launch alert"
                aria-label="Set launch alert"
                aria-pressed={showLaunchAlert}
                className={`relative z-10 flex items-center gap-1 rounded-lg px-2 py-1.5 transition-all duration-200 hover:scale-110 active:scale-95 ${
                  showLaunchAlert
                    ? 'bg-blue-500/20 text-blue-300 ring-1 ring-blue-500/40'
                    : 'text-text-tertiary hover:bg-blue-500/10 hover:text-blue-400'
                }`}
              >
                <Bell className="h-4 w-4" />
                <span className="hidden sm:inline text-xs font-medium">Alert</span>
              </button>
            )}

            {affiliateBrand && (
              <a
                href={buildAffiliateUrl(affiliateBrand, 'feed')}
                target="_blank"
                rel="noopener noreferrer"
                data-affiliate-partner={affiliateBrand}
                data-article-slug={slugify(post.title)}
                onClick={(e) => {
                  e.stopPropagation();
                  trackAffiliateClick(affiliateBrand, post.id, 'feed', undefined, post.category);
                  trackPriceIntent('price_cta_click', vehicleSlug, post.id, 'feed');
                  // ANALYTICS-063 + ANALYTICS-064: affiliate funnel with per-article attribution
                  trackAffiliateFunnelCTA(post.id, vehicleSlug, 'check_price', buildAffiliateUrl(affiliateBrand, 'feed'), affiliateBrand, 'feed', slugify(post.title));
                }}
                className="relative z-10 flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-emerald-400 transition-all duration-200 hover:bg-emerald-500/15 hover:text-emerald-300 active:scale-95 ring-1 ring-emerald-500/25"
              >
                <ShoppingCart className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Check Price</span>
              </a>
            )}

            {/* Insurance CTA */}
            {insuranceUrl && (
              <a
                href={insuranceUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-affiliate-partner="insurance"
                data-article-slug={slugify(post.title)}
                onClick={(e) => {
                  e.stopPropagation();
                  trackAffiliateClick('insurance', post.id, 'feed', undefined, post.category);
                  // ANALYTICS-063 + ANALYTICS-064: affiliate funnel with per-article attribution
                  trackAffiliateFunnelCTA(post.id, vehicleSlug, 'insurance', insuranceUrl, 'insurance', 'feed', slugify(post.title));
                }}
                className="relative z-10 flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-sky-400 transition-all duration-200 hover:bg-sky-500/15 hover:text-sky-300 active:scale-95 ring-1 ring-sky-500/25"
              >
                <Shield className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Insurance</span>
              </a>
            )}

            {/* UI-089 + UI-093: Direct share buttons — WhatsApp, X/Twitter, Copy Link */}
            <WhatsAppShareButton postId={post.id} postTitle={post.title} category={post.category} sourceId={post.source?.id} />
            <TwitterShareButton postId={post.id} postTitle={post.title} category={post.category} sourceId={post.source?.id} />
            <CopyLinkButton postId={post.id} postTitle={post.title} category={post.category} sourceId={post.source?.id} />

            {/* Not Relevant — AI-002 feedback signal */}
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                // ANALYTICS-091: Track skip for sponsored cards
                if (isSponsored) {
                  trackSponsoredCardSkip(post.id, index, post.sponsorBrand ?? '', post.category);
                }
                notRelevantMutation.mutate();
              }}
              disabled={notRelevantMutation.isPending}
              title="Not relevant — hide and improve recommendations"
              aria-label="Mark not relevant"
              className="relative z-10 rounded-lg p-2 text-text-tertiary transition-all duration-200 hover:bg-rose-500/15 hover:text-rose-400 hover:scale-110 active:scale-95 disabled:opacity-50"
            >
              <ThumbsDown className="h-4 w-4" />
            </button>

            {/* External Link — ANALYTICS-026: track outbound click.
                T-209: absent on our own editorial — the url is an /article/
                marker, not an address, and the card already goes to the piece. */}
            {outboundUrl(post) && (
            <a
              href={outboundUrl(post)!}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                e.stopPropagation();
                trackOutboundLinkClick(post.id, post.url, 'feed');
              }}
              className="relative z-10 rounded-lg p-2 text-text-tertiary transition-all duration-200 hover:bg-primary-500/10 hover:text-primary-400 hover:scale-110 active:scale-95"
            >
              <ExternalLink className="h-4 w-4" />
            </a>
            )}
          </div>

          {/* SEO-380: Inline launch-alert subscribe panel — opens on bell tap */}
          {isUpcoming && firstBrand && showLaunchAlert && (
            <div
              className="relative z-10"
              onClick={(e) => e.stopPropagation()}
            >
              <PriceDropAlert slug={slugify(post.title)} brand={firstBrand} />
            </div>
          )}
        </div>
      </div>
      {showGarageShare && (
        <GarageShareModal
          postId={post.id}
          source="feed"
          articleCategory={post.category}
          sourceDomain={post.source?.url || 'unknown'}
          sourceName={post.source?.name || 'unknown'}
          onClose={() => setShowGarageShare(false)}
          onSaved={(name) => setGarageSaveToast(name)}
        />
      )}
      {/* UI-063: Garage save confirmation toast */}
      {garageSaveToast && (
        <GarageSaveToast garageName={garageSaveToast} onDismiss={() => setGarageSaveToast(null)} />
      )}
      {/* UI-051: Post-follow confirmation toast */}
      <FollowConfirmationToast
        label={followToastLabel}
        onDismiss={() => setFollowToastLabel(null)}
      />
    </article>
  );
}
